/**
 * notificationsRead.ts — a notification the visitor acted on is read on the server too.
 *
 * Card 1219063265423680 (lane A for Blake, 2026-10-17): the bell carries a number
 * (the unread rows that count on it, app.js bellUnreadCount), the drawer paints
 * those rows as unread, and two things mark rows read:
 *
 *   1. MARK ALL AS READ, the control in the drawer's head (#ad-readall, painted
 *      only while the bell has a number): every row in the drawer flips read in
 *      memory, the page repaints (the drawer stays open and keeps its scroll:
 *      rerenderKeepScroll, data-keep-scroll="activity-list"), and the server is
 *      told POST /api/notifications/read-all, which marks every row of the
 *      visitor's, deal rows included, as the app's own Mark all as read does.
 *   2. A TAP ON A ROW ([data-adgo], Blake's handler goes where it goes today:
 *      the deal, the chat, the listing): that row flips read in memory before
 *      his handler repaints, and the server is told PATCH
 *      /api/notifications/:id/read. A deal or message row is told too (the
 *      server row is read), though the bell never counted it.
 *
 * PAINT FIRST, as dealsReadState (decision 3) and the app's store do
 * (artifacts/mobile/context/notificationStore.ts markNotificationRead). A row
 * that was already read owes nothing: no write, no re-read.
 *
 * WHEN THE SERVER AGREES (2xx) the paint is the truth and nothing more is read,
 * unless a re-read was out when the write left or started since (below): the
 * app's store does the same, and a re-read would repaint a page the visitor
 * may have moved on to (a tapped message opens its chat; the re-read rebuilt the
 * composer empty, found 2026-10-20 by the review).
 *
 * AFTER A REFUSAL OR AN UNREACHABLE SERVER the list is read again
 * (installNotifications, GET /api/notifications; both write routes ask their
 * cache to clear before answering, though a GET already in flight can recache
 * an older list for up to 15 s, a server race that is Nick's), so what stays
 * is what the server holds: the rows come back
 * unread, the refusal is said to the console only, never to the visitor. When
 * that re-read fails too (offline), the paint stays, as the app's store leaves
 * it. The re-read puts the rows back in memory and repaints only while nothing
 * on the page would be lost: not while a Deals composer holds words or any box
 * has focus (dealsReadState's drafting rule), unless the drawer is open, whose
 * rows index memory by position and must match it. A showing toast survives
 * the repaint (the bridge re-shows it). Re-reads go one at a time: one owed
 * while another is out goes after it. A write the server agrees to while a
 * re-read is out, or after one started since it left, would be undone when
 * that older answer lands, so it asks for one more re-read; only a write with
 * no re-read in play trusts its paint alone. A re-read made elsewhere (the
 * return-to-tab and timer re-read of card 1219177482800380) must go through
 * this seam's reread, or the guard cannot see it. Nothing guards a double tap:
 * the control is gone the instant the paint lands, and a row tapped again has
 * nothing left to mark; a row tapped while a Mark all is still out writes
 * nothing of its own (memory already shows it read), so if that Mark all is
 * then refused the row comes back unread until its next tap.
 *
 * The listener is a document capture click, like the other seams, and it never
 * stops the event: Blake's own row handler must still run. There is no handler
 * of his on the control, so no GUARDED_WRITES entry is needed (writeGuard.ts).
 */

import { installNotifications } from "./floorTransport";

type ReadWindow = Window & {
  __gbzMarkNotificationsRead?: (index: number | null, repaint: boolean) => string[];
};

const READ_ALL_SELECTOR = "#ad-readall";
const ROW_SELECTOR = "[data-adgo]";
/** The composers a repaint would rebuild empty (dealsReadState.ts COMPOSERS). */
const COMPOSERS = ["#msg-in", "#msgrail-in"];

export function installNotificationsRead(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId?: string | null,
): () => void {
  const w = win as ReadWindow;
  const doc = win.document;
  let stopped = false;
  let rereading = false;
  let owed = false;
  let reads = 0;

  const drafting = (): boolean => {
    for (const selector of COMPOSERS) {
      if ((doc.querySelector(selector) as HTMLInputElement | null)?.value) return true;
    }
    const el = doc.activeElement as HTMLElement | null;
    return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
  };

  const drawerOpen = (): boolean => !!doc.querySelector(".activity-drawer.open");

  /* What the server holds, after a refusal, or after an agreement an older
     re-read could undo. One at a time; repaints only when nothing would be lost
     (decided when the rows land, not when the read goes), always while the
     drawer is open. */
  const reread = async (): Promise<void> => {
    if (stopped) return;
    if (rereading) { owed = true; return; }
    rereading = true;
    reads += 1;
    try {
      await installNotifications(win, authFetch, myUserId, { repaint: () => drawerOpen() || !drafting() });
    } catch (err) {
      console.warn("[web-install] notifications: re-read failed:", err);
    } finally {
      rereading = false;
      if (owed) { owed = false; void reread(); }
    }
  };

  /* Tells the server. Agreement needs nothing more unless a re-read was out when
     the write left (outAtSend) or started since (reads moved); a refusal or an
     unreachable server is said to the console and the list is read again. */
  const send = (what: string, request: () => Promise<Response>): void => {
    const readsAtSend = reads;
    const outAtSend = rereading;
    void Promise.resolve()
      .then(request)
      .then((res) => {
        if (res.ok) {
          if (outAtSend || reads !== readsAtSend) void reread();
          return;
        }
        console.warn(`[web-install] notifications: ${what} not marked read (${res.status})`);
        void reread();
      })
      .catch((err) => {
        console.warn(`[web-install] notifications: ${what} not marked read:`, err);
        void reread();
      });
  };

  const onClick = (e: Event): void => {
    if (stopped) return;
    const target = e.target as Element | null;
    if (!target || typeof target.closest !== "function") return;
    const mark = w.__gbzMarkNotificationsRead;
    if (typeof mark !== "function") return;
    if (target.closest(READ_ALL_SELECTOR)) {
      const ids = mark(null, true);
      if (ids.length === 0) return;
      send("the drawer", () => authFetch("/api/notifications/read-all", { method: "POST" }));
      return;
    }
    const rowEl = target.closest(ROW_SELECTOR) as HTMLElement | null;
    if (!rowEl) return;
    const index = parseInt(rowEl.dataset.adgo ?? "", 10);
    if (!Number.isFinite(index)) return;
    const ids = mark(index, false);
    if (ids.length === 0) return;
    const id = ids[0];
    send(`row ${id}`, () => authFetch(`/api/notifications/${encodeURIComponent(id)}/read`, { method: "PATCH" }));
  };

  doc.addEventListener("click", onClick, true);
  return () => {
    stopped = true;
    doc.removeEventListener("click", onClick, true);
  };
}
